import type { TypeWindow } from 'geoview-core/core/types/global-types';
import { useTranslation } from 'geoview-core/core/translation/i18n';
import { logger } from 'geoview-core/core/utils/logger';
import { Fetch } from 'geoview-core/core/utils/fetch-helper';

import { getSxClasses } from './about-panel-style';
import Markdown from 'markdown-to-jsx';

import type {
  AboutPanelProps,
  TypeDefaultContentProps,
  TypeMarkdownFromContentProps,
  TypeMarkdownFromPathProps,
} from './about-panel-types';

// TODO: Optimize CSS see issue #3477

/**
 * Custom link component that opens in a new tab with screen reader announcement.
 *
 * @param props - Standard anchor element props
 * @returns An anchor element with accessible external link indicator
 */
function ExternalLink({ children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>): JSX.Element {
  logger.logTraceRender('geoview-about-panel/about-panel > ExternalLink');

  const { t } = useTranslation<string>();

  return (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
      <span className="visually-hidden">{t('general.opensInNewTab')}</span>
    </a>
  );
}

/** Markdown options for both file-based and content-based rendering. Ensures all links open in external tabs with accessibility announcements. */
const MARKDOWN_OPTIONS = {
  overrides: {
    a: {
      component: ExternalLink,
    },
  },
};

/**
 * Component to render markdown content from a file path / markdown document.
 *
 * @param props - The component props
 * @returns The created JSX Element from the MD document
 * @throws {Error} When the markdown file cannot be fetched
 */
function MarkdownFromPath(props: TypeMarkdownFromPathProps): JSX.Element {
  logger.logTraceRender('geoview-about-panel/about-panel > MarkdownFromPath');

  const { mdPath } = props;
  const { cgpv } = window as TypeWindow;
  const { ui, reactUtilities } = cgpv;
  const { react } = reactUtilities;
  const { useEffect, useState } = react;
  const { Box, Typography } = ui.elements;

  const [content, setContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const theme = ui.useTheme();
  const sxClasses = getSxClasses(theme);
  const { t } = useTranslation<string>();

  /**
   * Fetches and loads the markdown content when the path changes.
   */
  useEffect(() => {
    logger.logTraceUseEffect('ABOUT PANEL - FETCH MARKDOWN', mdPath);

    // Fetch the content and update the state accordingly, asynchronously, upon response
    Fetch.fetchText(mdPath)
      .then((text: string): void => {
        setContent(text);
      })
      .catch((fetchError: unknown): void => {
        const errorMessage = fetchError instanceof Error ? fetchError.message : 'Unknown error';
        logger.logError('Error fetching markdown file:', fetchError);
        setError(errorMessage);
      });
  }, [mdPath]);

  if (error) {
    return (
      <Box sx={sxClasses.errorContainer}>
        <Typography color="error">
          {t('AboutPanel.failed')}
          {error}
        </Typography>
      </Box>
    );
  }

  if (!content) {
    return (
      <Box sx={sxClasses.loadingContainer}>
        <Typography>{t('AboutPanel.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={sxClasses.markdownContainer}>
      <Markdown options={MARKDOWN_OPTIONS}>{content}</Markdown>
    </Box>
  );
}

/**
 * Component to render markdown content from an array of strings.
 *
 * @param props - The component props
 * @returns The created JSX Element from the MD content
 */
function MarkdownFromContent(props: TypeMarkdownFromContentProps): JSX.Element {
  logger.logTraceRender('geoview-about-panel/about-panel > MarkdownFromContent');

  const { mdContent } = props;
  const { cgpv } = window as TypeWindow;
  const { ui } = cgpv;
  const { Box } = ui.elements;

  const theme = ui.useTheme();
  const sxClasses = getSxClasses(theme);

  return (
    <Box sx={sxClasses.markdownContainer}>
      {mdContent.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index} sx={sxClasses.markdownItem}>
          <Markdown options={MARKDOWN_OPTIONS}>{item}</Markdown>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Component to render default about panel content.
 *
 * @param props - The component props
 * @returns The created JSX Element from the configuration options
 */
function DefaultContent(props: TypeDefaultContentProps): JSX.Element {
  logger.logTraceRender('geoview-about-panel/about-panel > DefaultContent');

  const { title, logoPath, description, link } = props;
  const { cgpv } = window as TypeWindow;
  const { ui } = cgpv;
  const { Box, Typography, Link } = ui.elements;

  const theme = ui.useTheme();
  const sxClasses = getSxClasses(theme);
  const { t } = useTranslation<string>();

  return (
    <Box sx={sxClasses.defaultContainer}>
      {title && (
        <Typography variant="h3" component="h3" sx={sxClasses.title}>
          {title}
        </Typography>
      )}

      {logoPath && (
        <Box sx={sxClasses.logoContainer}>
          <img src={logoPath} alt={title || 'Logo'} />
        </Box>
      )}

      {description && (
        <Typography variant="body1" sx={sxClasses.description}>
          {description}
        </Typography>
      )}

      {link && (
        <Box sx={sxClasses.linkContainer}>
          <Link href={link} underline="hover" target="_blank" rel="noopener noreferrer">
            {link}
            <span className="visually-hidden">{t('general.opensInNewTab')}</span>
          </Link>
        </Box>
      )}
    </Box>
  );
}

/**
 * Main About Panel component.
 *
 * Renders markdown content from path, array of strings, or default content.
 *
 * @param props - The component props
 * @returns The About Panel element
 */
export function AboutPanel(props: AboutPanelProps): JSX.Element {
  logger.logTraceRender('geoview-about-panel/about-panel');

  const { config } = props;

  const { cgpv } = window as TypeWindow;
  const { ui } = cgpv;
  const { Box } = ui.elements;

  const theme = ui.useTheme();
  const sxClasses = getSxClasses(theme);

  // Priority 1: Markdown file path
  if (config.mdPath) {
    return (
      <Box sx={sxClasses.container}>
        <MarkdownFromPath mdPath={config.mdPath} />
      </Box>
    );
  }

  // Priority 2: Markdown content array
  if (config.mdContent && config.mdContent.length > 0) {
    return (
      <Box sx={sxClasses.container}>
        <MarkdownFromContent mdContent={config.mdContent} />
      </Box>
    );
  }

  // Priority 3: Default content (title, logo, description, link)
  return (
    <Box sx={sxClasses.container}>
      <DefaultContent title={config.title} logoPath={config.logoPath} description={config.description} link={config.link} />
    </Box>
  );
}
